import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useWarehouseContext } from "./WarehouseProvider";
import { ReactComponent as CameraSvg } from "./svgs/camera.svg";
import { StepHeader } from "./components/StepHeader";
import { PhotoConfirmation } from "./components/PhotoConfirmation";
import { FullscreenModal } from "./components/FullscreenModal";
import { Webcam } from "./components/Webcam";
import { useSteps } from "./useSteps";
import { RejectedBanner } from "./components/RejectedBanner";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

const TITLE = "Photo Non-Conform Overview";

export const NonConformPhoto = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState("");

  const { nonConformPictures, addNonConformPicture, currentTakeback } =
    useWarehouseContext();
  const { next, previous } = useSteps();
  const webcamRef = useRef(null);
  const takePhotoButtonRef = useRef(null);

  const handleCapturePackage = useCallback(
    async (picture) => {
      addNonConformPicture(picture);
    },
    [addNonConformPicture]
  );

  const capturePhoto = useCallback(() => {
    const photo = webcamRef.current?.getScreenshot();
    if (photo) {
      setCapturedPhoto(photo);
      setShowConfirmation(true);
    }
  }, []);

  const handleConfirmPhoto = useCallback(() => {
    const isFirstPhoto = nonConformPictures.length === 0;
    handleCapturePackage(capturedPhoto);
    setShowConfirmation(false);
    setCapturedPhoto("");
    // Auto-advance to next step after first photo
    if (isFirstPhoto) {
      next();
    }
  }, [capturedPhoto, handleCapturePackage, nonConformPictures.length, next]);

  const handleCancelPhoto = useCallback(() => {
    setShowConfirmation(false);
    setCapturedPhoto("");
  }, []);

  const handleBack = useCallback(() => {
    setShowConfirmation(false);
    setCapturedPhoto("");
  }, []);

  // Handle Enter key navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !isLoading) {
        e.preventDefault();

        // If showing confirmation, confirm the photo
        if (showConfirmation) {
          handleConfirmPhoto();
        }
        // Otherwise, take a photo
        else {
          capturePhoto();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    nonConformPictures.length,
    next,
    isLoading,
    showConfirmation,
    handleConfirmPhoto,
    capturePhoto,
  ]);

  // Get reject category from takeback data
  const takebackData =
    typeof currentTakeback?.data === "object"
      ? currentTakeback?.data
      : JSON.parse(currentTakeback?.data || "{}");
  const rejectCategory = takebackData?.rejectCategory;

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <Webcam ref={webcamRef} setIsLoading={setIsLoading} />
        <StepHeader opacity="100%" isCameraActive />
        {rejectCategory && (
          <div className="relative z-50">
            <RejectedBanner rejectCategory={rejectCategory} />
          </div>
        )}

        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-6 max-w-[672px]">
            <div className="flex flex-col items-center gap-4">
              <h1 className="text-[29px] font-bold text-white leading-[37px] tracking-[-0.01em]">
                {TITLE}
              </h1>
              <p className="text-[18px] font-normal text-[#C6CCE8] leading-[23px] text-center max-w-[600px]">
                Spread the non-conforming contents out on the sorting surface
                and take a top down photo.
              </p>
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter isCameraActive>
          <WarehouseButtonGroup columns={nonConformPictures.length > 0 ? 3 : 2}>
            <button
              onClick={previous}
              className="w-full rounded-lg font-normal py-3.5 m-0 border border-transparent bg-white text-black hover:bg-gray-200"
            >
              Back
            </button>
            <WarehouseButton
              ref={takePhotoButtonRef}
              variant="primary"
              onClick={capturePhoto}
              disabled={isLoading}
            >
              <div className="flex items-center justify-center gap-2">
                <CameraSvg className="fill-white w-5 h-5" />
                <span>Take Photo of All Non-Conform</span>
              </div>
            </WarehouseButton>
            {nonConformPictures.length > 0 && (
              <WarehouseButton variant="primary" onClick={next}>
                Continue
              </WarehouseButton>
            )}
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
      {showConfirmation &&
        createPortal(
          <PhotoConfirmation
            title={TITLE}
            photo={capturedPhoto}
            onConfirm={handleConfirmPhoto}
            onCancel={handleCancelPhoto}
            onBack={handleBack}
            retakeButtonText="Retake Photo of All Non-Conform"
          />,
          document.body
        )}
    </FullscreenModal>
  );
};

NonConformPhoto.title = TITLE;
